package com.taocent.simple.compose.component.richtext.core.internal.ui

import androidx.compose.foundation.layout.Spacer
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.withTransform
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.style.BaselineShift
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.TextUnit
import com.taocent.simple.compose.component.richtext.core.internal.emoji.CUSTOM_EMOJI_TAG

internal data class EmojiDrawSpec(
    val id: String,
    val icon: ImageVector,
    val start: Int,
    val lineColor: Color,
    val fontSize: TextUnit,
    val baselineShift: BaselineShift?,
    val hasItalic: Boolean,
    val hasStrikethrough: Boolean,
    val hasUnderline: Boolean,
)

/**
 * 把 [drawSpecs] 按 `(id, fontSize)` 复合 key 拆成 painter 用 specs。
 *
 * 修复背景:之前 `painterSpecs = drawSpecs.distinctBy { it.id }` 只会按 id 拆,
 * 同 id 不同 fontSize 的 emoji 共享一个 `VectorPainter` 实例。
 * `VectorPainter` 内部 `RenderNode` 缓存不包含 size,导致同 painter 在多次
 * 不同 size 的 `draw(size)` 时,RenderNode 复用,几何/纹理沿用第一次的 size,
 * 出现"小字号叠在大字号上"或反之的渲染 bug。改为按 `(id, fontSize)` 拆后,
 * 每个 size 独立 painter,避免跨 size 复用 RenderNode。
 */
internal fun computeEmojiPainterSpecs(
    drawSpecs: List<EmojiDrawSpec>,
): List<EmojiDrawSpec> = drawSpecs.distinctBy { it.id to it.fontSize }

private data class EmojiLayoutBounds(
    val box: Rect,
    val baseline: Float,
)

private data class EmojiDrawItem(
    val id: String,
    val left: Float,
    val top: Float,
    val size: Float,
    val fontSize: TextUnit,
    val lineColor: Color,
    val hasItalic: Boolean,
    val hasStrikethrough: Boolean,
    val hasUnderline: Boolean,
)

/**
 * 自定义 emoji 画在 BasicTextField 之上的 Canvas 覆盖层。
 *
 * 修复背景:之前直接读 `layoutResult: TextLayoutResult?` 在 Composable 重组阶段
 * 计算 drawItems,在 [emoji][光标] 场景下在光标处插入 emoji 时,会出现"光标下方
 * 所有包含 emoji 的行渲染往右闪动然后回原地"的 bug。
 *
 * 根因:`onTextLayout` 在 Measure pass 末尾触发,而 `CustomEmojiCanvasOverlay`
 * 在 Composable 重组阶段(Measure pass 之前)就计算 drawItems,导致第一次重组
 * 用的是**旧** `layoutResult` 算 emoji 的 `bounds.box.left`,而新 layoutResult
 * 算出的位置不一样。光标当前行和上方行的 emoji `start` offset 没变,所以不闪;
 * 光标下方行的 emoji `start` offset 因为前面多了新 emoji 字符而变化,所以
 * `getBoundingBox(newStart)` 在旧 layoutResult 下返回错位的 left,产生闪动。
 *
 * 修复:把 `drawItems` 计算移到 [drawWithContent] 的 `onDraw` 闭包内,接收
 * [layoutResultState] 而不是快照,这样 drawItems 始终用当前帧的 `layoutResult`。
 * `drawWithContent.onDraw` 跑在 Draw pass(Measure pass 之后),`layoutResult`
 * 一定是当前帧的最新值。`annotatedString` 变化时 Composable 重组,但不再依赖
 * `layoutResult`(纯函数算 drawSpecs),`textLayoutResult` 变化时 onDraw 重画
 * (不重组)。完全消除"stale layoutResult"窗口。
 */
@Composable
fun CustomEmojiCanvasOverlay(
    annotatedString: AnnotatedString,
    layoutResultState: State<TextLayoutResult?>,
    emojiIcons: Map<String, ImageVector>,
    primaryColor: Color,
    currentColor: Color,
    modifier: Modifier = Modifier,
) {
    val density = LocalDensity.current
    // drawSpecs 只依赖 annotatedString / emojiIcons / color,纯函数计算,无 stale 风险
    val drawSpecs = remember(annotatedString, emojiIcons, primaryColor, currentColor) {
        val emojiAnnotations = annotatedString.getStringAnnotations(CUSTOM_EMOJI_TAG, 0, annotatedString.length)
        emojiAnnotations.mapNotNull { range ->
            val icon = emojiIcons[range.item] ?: return@mapNotNull null
            if (range.start !in 0 until annotatedString.length) return@mapNotNull null
            val spanStyle = annotatedString.spanStyles
                .lastOrNull { it.start <= range.start && it.end > range.start }
                ?.item
            val lineColor = spanStyle?.color
                ?.takeIf { it != Color.Unspecified && it != Color.Transparent }
                ?: currentColor.takeIf { it != Color.Unspecified }
                ?: primaryColor
            val textDecoration = spanStyle?.textDecoration
            EmojiDrawSpec(
                id = range.item,
                icon = icon,
                start = range.start,
                lineColor = lineColor,
                fontSize = spanStyle?.fontSize ?: TextUnit.Unspecified,
                baselineShift = spanStyle?.baselineShift?.takeIf { it != BaselineShift.None },
                hasItalic = spanStyle?.fontStyle == FontStyle.Italic,
                hasStrikethrough = textDecoration?.contains(TextDecoration.LineThrough) == true,
                hasUnderline = textDecoration?.contains(TextDecoration.Underline) == true,
            )
        }
    }
    if (drawSpecs.isEmpty()) return

    // painter cache 按 (id, fontSize) 复合 key 拆,避免 VectorPainter 跨 size 复用 RenderNode
    val painterSpecs = remember(drawSpecs) { computeEmojiPainterSpecs(drawSpecs) }
    val painters = painterSpecs.associate { spec ->
        spec.id to spec.fontSize to rememberVectorPainter(spec.icon)
    }

    // 关键:用 Spacer + drawWithContent 替代 Canvas,drawItems 在 onDraw 闭包内算
    // onDraw 跑在 Draw pass(Measure pass 之后),layoutResultState.value 一定是当前帧的最新值
    Spacer(
        modifier = modifier.drawWithContent {
            val currentLayoutResult = layoutResultState.value ?: return@drawWithContent

            val drawItems = drawSpecs.mapNotNull { spec ->
                val bounds = runCatching {
                    val line = currentLayoutResult.getLineForOffset(spec.start)
                    EmojiLayoutBounds(
                        box = currentLayoutResult.getBoundingBox(spec.start),
                        baseline = currentLayoutResult.getLineBaseline(line)
                    )
                }.getOrNull() ?: return@mapNotNull null
                val emojiSize = spec.fontSize
                    .takeIf { it != TextUnit.Unspecified }
                    ?.let { with(density) { it.toPx() } }
                    ?: bounds.box.height
                val baselineShiftValue = spec.baselineShift?.multiplier ?: 0f
                EmojiDrawItem(
                    id = spec.id,
                    left = bounds.box.left,
                    top = bounds.baseline - emojiSize * (0.85f + baselineShiftValue),
                    size = emojiSize,
                    fontSize = spec.fontSize,
                    lineColor = spec.lineColor,
                    hasItalic = spec.hasItalic,
                    hasStrikethrough = spec.hasStrikethrough,
                    hasUnderline = spec.hasUnderline,
                )
            }
            if (drawItems.isEmpty()) return@drawWithContent

            drawItems.forEach { item ->
                val painter = painters[item.id to item.fontSize] ?: return@forEach
                drawCustomEmojiItem(
                    item = item,
                    painter = painter,
                    tintColor = primaryColor,
                )
            }
        }
    )
}

private fun DrawScope.drawCustomEmojiItem(
    item: EmojiDrawItem,
    painter: Painter,
    tintColor: Color,
) {
    withTransform({
        translate(left = item.left, top = item.top)
        if (item.hasItalic) {
            rotate(degrees = -8f, pivot = Offset(item.size / 2f, item.size / 2f))
        }
    }) {
        with(painter) {
            draw(
                size = Size(item.size, item.size),
                colorFilter = ColorFilter.tint(tintColor)
            )
        }
        val strokeWidth = item.size * 0.08f
        if (item.hasStrikethrough) {
            drawLine(
                color = item.lineColor,
                start = Offset(0f, item.size / 2f),
                end = Offset(item.size, item.size / 2f),
                strokeWidth = strokeWidth
            )
        }
        if (item.hasUnderline) {
            drawLine(
                color = item.lineColor,
                start = Offset(0f, item.size - strokeWidth),
                end = Offset(item.size, item.size - strokeWidth),
                strokeWidth = strokeWidth
            )
        }
    }
}
